import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, ChevronUp, Lightbulb, Sparkles } from 'lucide-react';
import { EAJourneyBarFrame } from '../../components/EAJourneyBarFrame';
import { EAPersonalizationCard } from '../../components/EAPersonalizationCard';
import { EAContextReviewCard } from '../../components/EAContextReviewCard';
import { useEA } from '../../contexts/EAContext';
import { useCoverage } from '../../hooks/useCoverage';

const mockInferredContext = [
  {
    id: 'ehr-system',
    label: 'EHR System',
    value: 'Epic (implemented 2019)',
    source: 'inferred',
    confidence: 'medium',
    category: 'Inferred',
  },
  {
    id: 'budget-range',
    label: 'Estimated Analytics Budget',
    value: '$2-5M annually',
    source: 'inferred',
    confidence: 'low',
    category: 'Inferred',
  },
  {
    id: 'team-size',
    label: 'Analytics Team Size',
    value: '15-20 FTEs',
    source: 'inferred',
    confidence: 'medium',
    category: 'Inferred',
  },
];

function ExistingInitiativesCard({ onClick }) {
  return (
    <div className="bg-white rounded-2xl border-2 border-[#e5e7eb] p-6 hover:shadow-lg cursor-pointer transition-all duration-300 flex flex-col h-full">
      <div className="flex items-start justify-between mb-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#18365E]/5">
          <Sparkles className="w-5 h-5 text-[#18365E]" />
        </div>
        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#18365E]/10 text-[#18365E]">
          Advanced
        </span>
      </div>

      <h3 className="text-base font-bold text-[#18365E] mb-2">Existing Initiatives</h3>

      <p className="text-sm text-[#717182] mb-5 leading-relaxed flex-1">
        Bring initiatives you’re already evaluating to see how they compare, overlap, or differ from DVA’s recommendations.
      </p>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all bg-[#18365E] text-white hover:bg-[#18365E]/90"
      >
        Add Existing Initiatives →
      </button>
    </div>
  );
}

export default function EAPersonalizationVariantB() {
  const navigate = useNavigate();
  const { edits, flags } = useEA();
  const [showInferredContext, setShowInferredContext] = useState(false);

  // Shared state + shared formula — no variant-local vector or weights.
  // A/B variants read coverage, like every other surface. The composite percentage and
  // the completion vector they used to render no longer exist.
  const { aggregates: coverageAggregates } = useCoverage();


  const handleProfileClick = () => {
    console.log('[EA] Open Profile Editor');
  };

  const handleSurveysClick = () => {
    console.log('[EA] Open Surveys');
  };

  const handleDocumentsClick = () => {
    console.log('[EA] Open Document Upload');
  };

  const handleExistingInitiativesClick = () => {
    console.log('[EA] Add Existing Initiatives');
  };

  const totalFlags = flags.filter((f) => f.targetType === 'context').length;
  const totalEdits = edits.filter((e) => e.targetType === 'context').length;

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <EAJourneyBarFrame sticky />

      <main className="mx-auto px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <EAPersonalizationCard
            type="profile"
            status="complete"
            onClick={handleProfileClick}
          />
          <EAPersonalizationCard
            type="surveys"
            status="complete"
            onClick={handleSurveysClick}
          />
          <EAPersonalizationCard
            type="documents"
            status={coverageAggregates.counts.evidence > 0 ? 'in_progress' : 'not_started'}
            onClick={handleDocumentsClick}
          />
        </div>

        <div className="flex justify-center mb-4">
          <div className="w-full md:max-w-[420px]">
            <ExistingInitiativesCard onClick={handleExistingInitiativesClick} />
          </div>
        </div>

        {/* Coverage, in place of the old Overall Personalization percentage and its
            0.4 / 0.5 / 0.1 weight strip. Two counts that name what they count. */}
        <div className="bg-white rounded-xl border border-[#e5e7eb] p-4 mb-4">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h2 className="text-sm font-semibold text-[#18365E]">Measure Coverage</h2>
              <p className="text-xs text-[#717182]">How much of the scoring library your inputs reach.</p>
            </div>
            <span className="text-xl font-bold text-[#18365E] tabular-nums">
              {coverageAggregates.breadth} of {coverageAggregates.total}
            </span>
          </div>
          <p className="text-xs text-[#717182]">
            {coverageAggregates.counts.evidence} evidence-backed from your documents
          </p>
        </div>

        <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden mb-4">
          <button
            onClick={() => setShowInferredContext(!showInferredContext)}
            className="w-full flex items-center justify-between p-3 hover:bg-[#F5F7FA] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-[#FF6E4C]" />
              <div className="text-left">
                <h3 className="text-sm font-semibold text-[#18365E]">Refine Inferred Context</h3>
                <p className="text-xs text-[#717182]">
                  {mockInferredContext.length} items inferred
                  {(totalFlags > 0 || totalEdits > 0) && (
                    <span className="ml-2 text-[#FF6E4C]">
                      • {totalFlags > 0 && `${totalFlags} flagged`}
                      {totalFlags > 0 && totalEdits > 0 && ', '}
                      {totalEdits > 0 && `${totalEdits} edited`}
                    </span>
                  )}
                </p>
              </div>
            </div>
            {showInferredContext ? (
              <ChevronUp className="w-4 h-4 text-[#717182]" />
            ) : (
              <ChevronDown className="w-4 h-4 text-[#717182]" />
            )}
          </button>

          {showInferredContext && (
            <div className="p-3 pt-0 border-t border-[#e5e7eb]">
              <p className="text-xs text-[#717182] mb-3 mt-3 leading-relaxed">
                DVA inferred a small number of assumptions where inputs were incomplete. Review only if something
                looks off.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {mockInferredContext.map((attr) => (
                  <EAContextReviewCard key={attr.id} attribute={attr} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 pt-3">
          <div className="text-right max-w-md">
            <p className="text-xs text-[#717182] leading-relaxed">
              Your inputs are strong enough to generate meaningful, tailored opportunities.
              <br />
              You can refine further at any time.
            </p>
          </div>

          <button
            onClick={() => navigate('/opportunities')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold shadow-lg transition-all bg-[#18365E] text-white hover:bg-[#18365E]/90"
          >
            Continue to Opportunities
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </main>
    </div>
  );
}
